import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Megaphone, QrCode, Activity, 
  Gift, Settings, LogOut, Menu, X, Building2, Bell, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ChatbotWidget from '../components/ChatbotWidget';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/');
  };

  const navItems = [
    { to: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Panel', end: true },
    { to: '/dashboard/campaigns', icon: <Megaphone className="w-5 h-5" />, label: 'Campañas' },
    { to: '/dashboard/devices', icon: <QrCode className="w-5 h-5" />, label: 'QR / Dispositivos' },
    { to: '/dashboard/locations', icon: <Building2 className="w-5 h-5" />, label: 'Locales' },
    { to: '/dashboard/activity', icon: <Activity className="w-5 h-5" />, label: 'Actividad' },
    { to: '/dashboard/vouchers', icon: <Gift className="w-5 h-5" />, label: 'Vouchers' },
    { to: '/dashboard/settings', icon: <Settings className="w-5 h-5" />, label: 'Configuración' },
  ];

  const NavItem = ({ to, icon, label, end }) => (
    <NavLink
      to={to}
      end={end}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) => `
        flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
        ${isActive 
          ? 'bg-primary text-white font-semibold shadow-md' 
          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
        }
      `}
      data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
    >
      {icon}
      <span className="font-medium">{label}</span>
    </NavLink>
  );

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'growth': return 'bg-blue-100 text-blue-700';
      case 'scale': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="h-10 w-10"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <img 
            src="https://customer-assets.emergentagent.com/job_796fbc31-c145-4a59-88df-6b2e6dbd2615/artifacts/n7zu75fp_file_00000000fb6c7243ac605c38dbc5a990%20%281%29.png" 
            alt="Stampply Rewards" 
            className="h-12 w-auto"
          />
        </div>
        <Button variant="ghost" size="icon" className="h-10 w-10">
          <Bell className="w-5 h-5 text-slate-500" />
        </Button>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-40 h-screen w-72 bg-white border-r border-slate-200
        transform transition-transform duration-300 ease-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        shadow-xl lg:shadow-none
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-24 flex items-center justify-center px-6 border-b border-slate-100">
            <img 
              src="https://customer-assets.emergentagent.com/job_796fbc31-c145-4a59-88df-6b2e6dbd2615/artifacts/n7zu75fp_file_00000000fb6c7243ac605c38dbc5a990%20%281%29.png" 
              alt="Stampply Rewards" 
              className="h-20 w-auto"
            />
          </div>

          {/* User Info */}
          <div className="p-4">
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
              <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-lg">
                  {user?.business_name?.charAt(0)?.toUpperCase() || 'M'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate text-sm">
                  {user?.business_name || 'Mi Negocio'}
                </p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getPlanBadgeColor(user?.plan)}`}>
                  Plan {user?.plan || 'Free'}
                </span>
              </div>
            </div>
            
            {/* Trial Banner */}
            {user?.status === 'trial' && user?.trial_ends_at && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-center gap-2 text-amber-800">
                  <Bell className="w-4 h-4" />
                  <span className="font-medium text-sm">Período de prueba</span>
                </div>
                <p className="text-amber-700 text-xs mt-1">
                  Te quedan {Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)))} días de {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}
                </p>
                <NavLink 
                  to="/dashboard/settings"
                  className="text-xs text-amber-600 hover:text-amber-800 font-medium mt-2 inline-block"
                >
                  Actualizar plan →
                </NavLink>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
            <p className="px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Menú principal
            </p>
            {navItems.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </nav>

          {/* Assistant Button */}
          <div className="px-4 py-2">
            <button
              onClick={() => {
                setChatbotOpen(true);
                setSidebarOpen(false);
              }}
              className="flex items-center gap-3 w-full px-4 py-3 text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-all duration-200"
              data-testid="assistant-menu-btn"
            >
              <MessageCircle className="w-5 h-5" />
              <span className="font-medium">Asistente IA</span>
            </button>
          </div>

          {/* Logout */}
          <div className="p-4 border-t border-slate-100">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Cerrar sesión</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <motion.div 
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen">
        <div className="pt-16 lg:pt-0">
          {/* Top bar for desktop */}
          <div className="hidden lg:flex h-16 bg-white border-b border-slate-200 items-center justify-between px-8">
            <div>
              {/* Breadcrumb or page title could go here */}
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <Bell className="w-5 h-5 text-slate-500" />
              </Button>
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">
                  {user?.business_name?.charAt(0)?.toUpperCase() || 'M'}
                </span>
              </div>
            </div>
          </div>
          
          {/* Page Content */}
          <div className="p-6 lg:p-8">
            <Outlet />
          </div>
        </div>
      </main>
      
      {/* Chatbot Widget */}
      <ChatbotWidget isOpen={chatbotOpen} onOpenChange={setChatbotOpen} />
    </div>
  );
};

export default DashboardLayout;
